/** TanStack Query client (ADR-06 §2.1). Server errors are final: no retry on an API error envelope. */
import { QueryCache, QueryClient } from "@tanstack/react-query";
import { isApiClientError } from "../api/client.js";
import { reportError } from "./telemetry.js";

/**
 * [C28] F-272: a read that fails unexpectedly (5xx, no response, or a client bug in the query function) must never
 * look like "no data" — the shell shows it as an error toast with the trace reference (D-259). Business refusals
 * (4xx) stay with the screens that handle them.
 */
type QueryErrorListener = (e: unknown) => void;
const queryErrorListeners = new Set<QueryErrorListener>();
export function onUnexpectedQueryError(l: QueryErrorListener): () => void {
  queryErrorListeners.add(l);
  return () => queryErrorListeners.delete(l);
}
export function isUnexpected(e: unknown): boolean {
  return isApiClientError(e) ? e.status >= 500 || e.status === 0 : true;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (e) => {
        if (!isUnexpected(e)) return;
        if (!isApiClientError(e)) reportError("client_error", e); // API failures were reported by the client
        for (const l of queryErrorListeners) l(e);
      },
    }),
    defaultOptions: {
      queries: {
        retry: (failureCount, error) => !isApiClientError(error) && failureCount < 2,
        refetchOnWindowFocus: false,
        staleTime: 30_000,
      },
      mutations: { retry: false },
    },
  });
}

/** Query keys, one place (D-203 fixtures and the containers share them). */
export const keys = {
  session: ["session"] as const,
  office: ["office"] as const,
  preferences: ["preferences"] as const,
  notifications: ["notifications"] as const,
  rates: ["rates"] as const,
  users: ["users"] as const,
  roles: ["roles"] as const,
  permissions: ["permissions"] as const,
  settings: ["settings"] as const,
  // C19
  accounts: ["accounts"] as const,
  statement: (id: string, q: unknown) => ["statement", id, q] as const,
  dashboard: ["dashboard"] as const,
  capitalOperations: ["capital-operations"] as const, // [C26]
  subscription: ["subscription"] as const, // [C27]
  approvals: ["approvals"] as const,
  // C20
  dollar: ["dollar"] as const,
  dollarOperations: (q: unknown) => ["dollar-operations", q] as const,
  // C21
  currencies: ["currencies"] as const,
  currencyOperations: (q: unknown) => ["currency-operations", q] as const,
  currencySettings: ["currency-settings"] as const,
  // C22
  customers: (archived: boolean) => ["customers", archived] as const,
  customerStatement: (id: string, q: unknown) => ["customer-statement", id, q] as const,
  // C23
  transfers: ["transfers"] as const,
  // [C33] D07 add-ons
  store: ["store"] as const,
  storeOperations: ["store", "operations"] as const,
  installments: ["installments"] as const,
  installment: (id: string) => ["installments", id] as const,
  journal: ["journal"] as const,
  // C25
  profits: ["profits"] as const,
  transferDetail: (id: string) => ["transfer-detail", id] as const,
  // C32
  shifts: ["shifts"] as const,
  days: ["days"] as const,
  shiftReport: (id: string) => ["shift-report", id] as const,
  dayReport: (id: string) => ["day-report", id] as const,
};
